/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Input,
  Table,
  Row,
  Col,
  message,
  Button,
  Select,
  Card,
  Space,
  Tag,
  Tooltip,
  Modal,
} from "antd";
import {
  SendOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import beepSound from "../../../assests/audio/beep.mp3";
import urls from "../../../api/urls";
import API from "../../../api";
import Heading from "../../../components/Heading";
import "./style.scss";

const { Option } = Select;

// Custom hook for barcode scanner detection
const useBarcodeScanner = (onScan, dependencies = []) => {
  const barcodeRef = useRef("");
  const intervalRef = useRef();

  // HTML5 API / Intent Integration for CipherLab and other enterprise browsers
  useEffect(() => {
    // 1. Global JS function injection (CipherLab "Web/HTML5" mode can trigger this)
    window.onBarcodeScanned = (barcode) => {
      if (barcode && typeof barcode === "string") {
        onScan(barcode.trim());
      }
    };
    window.onScanBarcode = window.onBarcodeScanned;
    window.CipherLabScan = window.onBarcodeScanned;

    // 2. Custom DOM Events (dispatched by some Enterprise Browsers or Wedge apps)
    const handleCustomScanEvent = (e) => {
      // Trying to find barcode data in common event detail paths
      const barcode =
        e.detail?.barcode ||
        e.detail?.data ||
        e.detail?.value ||
        e.detail ||
        e.data;
      if (typeof barcode === "string" && barcode.trim()) {
        onScan(barcode.trim());
      }
    };

    document.addEventListener("barcodeScanned", handleCustomScanEvent);
    document.addEventListener("cipherlab.barcode", handleCustomScanEvent);
    document.addEventListener("scannerCB", handleCustomScanEvent);

    return () => {
      window.onBarcodeScanned = undefined;
      window.onScanBarcode = undefined;
      window.CipherLabScan = undefined;
      document.removeEventListener("barcodeScanned", handleCustomScanEvent);
      document.removeEventListener("cipherlab.barcode", handleCustomScanEvent);
      document.removeEventListener("scannerCB", handleCustomScanEvent);
    };
  }, [onScan]);

  const handleKeyDown = useCallback(
    (event) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (event.code === "Enter") {
        if (barcodeRef.current.trim()) {
          onScan(barcodeRef.current.trim());
          barcodeRef.current = "";
        }
        return;
      }

      const ignoredKeys = [
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "CapsLock",
        "NumLock",
        "ScrollLock",
        "Tab",
        "Escape",
        "F1",
        "F2",
        "F3",
        "F4",
        "F5",
        "F6",
        "F7",
        "F8",
        "F9",
        "F10",
        "F11",
        "F12",
        "Insert",
        "Delete",
        "Home",
        "End",
        "PageUp",
        "PageDown",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Pause",
        "PrintScreen",
        "ContextMenu",
      ];

      if (
        ignoredKeys.includes(event.key) ||
        event.key.length > 1 ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }

      if (event.key.length === 1) {
        barcodeRef.current += event.key;
      }

      // Reset barcode after 150ms of inactivity to support slower Android wedge emulators
      intervalRef.current = setTimeout(() => {
        barcodeRef.current = "";
      }, 150);
    },
    [onScan, ...dependencies],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [handleKeyDown]);
};

// Custom hook for managing Out-Scan data
const useNewOutScanManager = () => {
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem("newOutScanData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        return {
          scannedShipments: parsed.scannedShipments || [],
          selectedAssignment: parsed.selectedAssignment || null,
        };
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
    return {
      scannedShipments: [],
      selectedAssignment: null,
    };
  };

  const initialData = loadFromStorage();
  const [scannedShipments, setScannedShipments] = useState(
    initialData.scannedShipments,
  );
  const [selectedAssignment, setSelectedAssignment] = useState(
    initialData.selectedAssignment,
  );

  const [scannedAWBs, setScannedAWBs] = useState(() => {
    const allAWBs = initialData.scannedShipments
      .map((item) => item.awbNumber?.toString().trim().toUpperCase())
      .filter(Boolean);
    return new Set(allAWBs);
  });

  // Save to localStorage whenever data changes
  useEffect(() => {
    const dataToSave = {
      scannedShipments,
      selectedAssignment,
    };
    localStorage.setItem("newOutScanData", JSON.stringify(dataToSave));
  }, [scannedShipments, selectedAssignment]);

  const addScannedShipment = useCallback(
    (awbNumber, shipmentData) => {
      const normalizedAWB = awbNumber.toString().trim().toUpperCase();

      const newShipment = {
        id: Date.now(),
        awbNumber: normalizedAWB,
        scannedAt: new Date().toISOString(),
        assignTo: selectedAssignment
          ? {
              type: selectedAssignment.type,
              id: selectedAssignment.id,
              name: selectedAssignment.name,
            }
          : null,
        ...shipmentData,
      };

      setScannedShipments((prev) => [...prev, newShipment]);
      setScannedAWBs((prev) => new Set([...prev, normalizedAWB]));
    },
    [selectedAssignment],
  );

  const removeScannedShipment = useCallback((awbNumber) => {
    const normalizedAWB = awbNumber.toString().trim().toUpperCase();
    setScannedShipments((prev) =>
      prev.filter((item) => item.awbNumber !== normalizedAWB),
    );
    setScannedAWBs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(normalizedAWB);
      return newSet;
    });
  }, []);

  const isAWBScanned = useCallback(
    (awbNumber) => {
      const normalizedAWB = awbNumber.toString().trim().toUpperCase();
      return scannedAWBs.has(normalizedAWB);
    },
    [scannedAWBs],
  );

  const clearAllData = useCallback(() => {
    setScannedShipments([]);
    setScannedAWBs(new Set());
    setSelectedAssignment(null);
    localStorage.removeItem("newOutScanData");
  }, []);

  const updateShipmentStatus = useCallback(
    (awbNumber, status, error = null) => {
      setScannedShipments((prev) =>
        prev.map((shipment) =>
          shipment.awbNumber === awbNumber
            ? { ...shipment, status, error }
            : shipment,
        ),
      );
    },
    [],
  );

  const updateSelectedAssignment = useCallback((assignment) => {
    setSelectedAssignment(assignment);
  }, []);

  return {
    scannedShipments,
    selectedAssignment,
    addScannedShipment,
    removeScannedShipment,
    isAWBScanned,
    clearAllData,
    updateSelectedAssignment,
    updateShipmentStatus,
    totalScannedCount: scannedShipments.length,
  };
};

// Custom hook for fetching destinations (Hubs and CPs)
const useDestinations = () => {
  const [hubs, setHubs] = useState([]);
  const [cps, setCps] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHubs = useCallback(async () => {
    try {
      setLoading(true);
      // Use the correct hub list API endpoint
      const response = await API.Api(`${urls.GET_HUB_LIST_FOR_OUTSCAN}`).getWithNoToken();
      if (response?.response?.data) {
        // API returns direct array of hub objects
        setHubs(response.response.data || []);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
      message.error("Failed to fetch hubs data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCPs = useCallback(async () => {
    try {
      setLoading(true);
      // Use the correct CP list API endpoint
      const response = await API.Api(`${urls.GET_CP_LIST_FOR_OUTSCAN}`).getWithNoToken();
      const cpData = response?.response?.data;
      if (Array.isArray(cpData)) {
        setCps(cpData || []);
      } else if (Array.isArray(cpData?.data)) {
        setCps(cpData.data || []);
      }
    } catch (error) {
      console.error("Error fetching CPs:", error);
      message.error("Failed to fetch CPs data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHubs();
    fetchCPs();
  }, [fetchHubs, fetchCPs]);

  return { hubs, cps, loading };
};

const NewOutScan = () => {
  const [currentAWB, setCurrentAWB] = useState("");
  const [loading, setLoading] = useState(false);
  const [destinationType, setDestinationType] = useState("hub");
  const [selectedDestination, setSelectedDestination] = useState(null);
  const awbInputRef = useRef(null);

  const {
    scannedShipments,
    selectedAssignment,
    addScannedShipment,
    removeScannedShipment,
    isAWBScanned,
    clearAllData,
    updateSelectedAssignment,
    updateShipmentStatus,
    totalScannedCount,
  } = useNewOutScanManager();

  const { hubs, cps, loading: destinationsLoading } = useDestinations();

  // Function to play beep sound
  const playBeepSound = useCallback(() => {
    try {
      const audio = new Audio(beepSound);
      audio.volume = 0.5;
      audio.play();
    } catch (error) {
      console.log("Audio creation failed:", error);
    }
  }, []);

  // Direct AWB processing without API call
  const processScannedAWB = useCallback((awbNumber) => {
    const shipmentData = {
      awbNumber,
      scannedAt: new Date().toISOString(),
    };

    return {
      status: true,
      data: shipmentData,
      message: `AWB ${awbNumber} scanned successfully`,
    };
  }, []);

  // Handle AWB scan with validation and deduplication
  const handleAWBScan = useCallback(
    (rawAwbNumber) => {
      const awbNumber = rawAwbNumber?.toString().trim().toUpperCase();

      if (!awbNumber || awbNumber.length === 0) {
        message.error("Please enter a valid AWB number");
        return;
      }

      // Check if assignment is selected
      if (!selectedAssignment) {
        message.error("Please select HUB/CP before scanning shipments");
        return;
      }

      const awbPattern = /^[A-Z0-9]+$/;
      if (!awbPattern.test(awbNumber)) {
        message.error("AWB number should contain only letters and numbers");
        return;
      }

      if (isAWBScanned(awbNumber)) {
        message.warning(`AWB ${awbNumber} has already been scanned`);
        return;
      }

      const result = processScannedAWB(awbNumber);

      if (result.status) {
        addScannedShipment(awbNumber, result.data);
        playBeepSound();
        message.success(
          result.message || `AWB ${awbNumber} scanned successfully`,
        );
        setCurrentAWB("");
      } else {
        message.error(result.error || result.message || "Failed to scan AWB");
      }
    },
    [
      processScannedAWB,
      addScannedShipment,
      isAWBScanned,
      playBeepSound,
      selectedAssignment,
    ],
  );

  // Set up barcode scanner
  useBarcodeScanner(handleAWBScan, [handleAWBScan]);

  // Handle destination selection
  const handleDestinationSelect = useCallback(
    (destinationId) => {
      const destinations = destinationType === "hub" ? hubs : cps;
      const selectedDest = destinations.find(
        (dest) => dest.premiseId === destinationId,
      );

      if (selectedDest) {
        const assignment = {
          type: destinationType.toUpperCase(),
          id: selectedDest.premiseId,
          name: selectedDest.premiseName,
        };
        setSelectedDestination(destinationId);
        updateSelectedAssignment(assignment);
        message.success(`Selected ${assignment.type}: ${assignment.name}`);

        // Focus on AWB input after selection
        setTimeout(() => {
          if (awbInputRef.current) {
            awbInputRef.current.focus();
          }
        }, 100);
      }
    },
    [destinationType, hubs, cps, updateSelectedAssignment],
  );

  // Handle destination type change
  const handleDestinationTypeChange = useCallback(
    (type) => {
      setDestinationType(type);
      setSelectedDestination(null);
      updateSelectedAssignment(null);
    },
    [updateSelectedAssignment],
  );

  // Clear all data including selection
  const handleClearAll = useCallback(() => {
    clearAllData();
    setSelectedDestination(null);
    setDestinationType("hub");
  }, [clearAllData]);

  // Submit Out-Scan
  const handleSubmitOutScan = useCallback(() => {
    if (!selectedAssignment) {
      message.error("Please select HUB/CP before submitting");
      return;
    }

    if (scannedShipments.length === 0) {
      message.error("No shipments scanned to submit");
      return;
    }

    // Get unique assignments from scanned shipments
    const uniqueAssignments = scannedShipments.reduce((acc, shipment) => {
      if (shipment.assignTo) {
        const key = `${shipment.assignTo.type}-${shipment.assignTo.id}`;
        if (!acc[key]) {
          acc[key] = {
            ...shipment.assignTo,
            count: 0,
          };
        }
        acc[key].count++;
      }
      return acc;
    }, {});

    const assignmentsList = Object.values(uniqueAssignments);

    Modal.confirm({
      title: "Confirm Out-Scan Submission",
      content: (
        <div>
          <p>
            Are you sure you want to submit the Out-Scan with the following
            details?
          </p>
          <div style={{ marginTop: "16px" }}>
            <p>
              <strong>Assignments:</strong>
            </p>
            {assignmentsList.length > 0 ? (
              <ul style={{ marginLeft: "20px", marginTop: "8px" }}>
                {assignmentsList.map((assignment, index) => (
                  <li key={index} style={{ marginBottom: "4px" }}>
                    <strong>
                      {assignment.type} - {assignment.name}
                    </strong>{" "}
                    ({assignment.count} shipment
                    {assignment.count > 1 ? "s" : ""})
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ marginLeft: "20px", color: "#ff4d4f" }}>
                No valid assignments found
              </p>
            )}
            <p style={{ marginTop: "12px" }}>
              <strong>Total Shipments:</strong> {scannedShipments.length}
            </p>
          </div>
        </div>
      ),
      icon: <ExclamationCircleOutlined />,
      okText: "Submit Out-Scan",
      cancelText: "Cancel",
      okType: "primary",
      width: 450,
      onOk: async () => {
        try {
          setLoading(true);

          // Group shipments by their actual assignments
          const assignmentGroups = scannedShipments.reduce((acc, shipment) => {
            if (shipment.assignTo) {
              const key = `${shipment.assignTo.type}-${shipment.assignTo.id}`;
              if (!acc[key]) {
                acc[key] = {
                  destinationType: shipment.assignTo.type.toUpperCase(),
                  destinationId: shipment.assignTo.id,
                  destinationName: shipment.assignTo.name,
                  shipments: [],
                };
              }
              acc[key].shipments.push(shipment.awbNumber);
            }
            return acc;
          }, {});

          const currentUserinfo = JSON.parse(localStorage.getItem("userinfo"));
          const payload = {
            spId: currentUserinfo?.spId || currentUserinfo?.premiseId,
            assignments: Object.values(assignmentGroups),
            sevasetuUserId: currentUserinfo?.sevasetuUserId,
          };

          const response = await API.Api(urls.CP_PRS_OUTSCAN).postIdHeader(
            payload,
          );

          // The API wrapper might return response in different structure
          let responseData = null;
          let statusCode = null;

          // Check different possible response structures
          if (response.response && response.response.data) {
            // Structure: response.response.data
            responseData = response.response.data;
            statusCode = response.response.status;
          } else if (response.data) {
            // Structure: response.data
            responseData = response.data;
            statusCode = response.status;
          } else {
            // Direct response
            responseData = response;
            statusCode = response.status;
          }

          // Handle response based on status code
          if (statusCode === 200 || responseData?.status === 200) {
            // Check if response has data structure and process accordingly
            if (responseData?.data) {
              const errors = responseData.data.errors || [];
              const successfulShipments = responseData.data.successful || [];
              const summary = responseData.data.summary || {
                totalProcessed: errors.length + successfulShipments.length,
                successful: successfulShipments.length,
                failed: errors.length,
              };

              // Process shipments - only if there are actual errors
              if (errors.length > 0) {
                // Update failed shipments with error status
                errors.forEach((error) => {
                  updateShipmentStatus(
                    error.documentNumber,
                    "error",
                    error.error,
                  );
                });

                // Update successful shipments
                successfulShipments.forEach((awb) => {
                  updateShipmentStatus(awb.documentNumber || awb, "success");
                });

                // Show backend message for errors
                message.warning({
                  content: (
                    <div>
                      <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                        📊 Out-Scan Summary
                      </div>
                      <div style={{ fontSize: "13px" }}>
                        {responseData?.message ||
                          `Out-Scan completed with errors. ${summary.successful} successful, ${summary.failed} failed.`}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          color: "#8c8c8c",
                        }}
                      >
                        Check the table below for detailed error information.
                      </div>
                    </div>
                  ),
                  duration: 10,
                  style: { marginTop: "50px" },
                });
              } else {
                // No errors - all successful or empty response
                if (successfulShipments.length > 0) {
                  // Update all successful shipments
                  successfulShipments.forEach((awb) => {
                    updateShipmentStatus(awb.documentNumber || awb, "success");
                  });

                  message.success(
                    responseData?.message ||
                      `Out-Scan completed successfully! ${summary.successful} shipment(s) processed.`,
                  );
                  setTimeout(() => handleClearAll(), 2000);
                } else {
                  // Complete success with no detailed shipment data
                  message.success(
                    responseData?.message || "Out-Scan completed successfully!",
                  );
                  handleClearAll();
                }
              }
            } else if (
              responseData?.message &&
              responseData.message.includes(
                "Processed 0 shipments successfully",
              )
            ) {
              // Handle case where no shipments were processed successfully
              scannedShipments.forEach((shipment) => {
                updateShipmentStatus(
                  shipment.awbNumber,
                  "error",
                  "Failed to process. Check with system administrator.",
                );
              });

              message.warning({
                content: (
                  <div>
                    <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                      ⚠️ Out-Scan Completed with Errors
                    </div>
                    <div style={{ fontSize: "13px" }}>
                      {responseData?.message}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        color: "#8c8c8c",
                      }}
                    >
                      Check the table below for detailed information.
                    </div>
                  </div>
                ),
                duration: 10,
                style: { marginTop: "50px" },
              });
            } else {
              // No errors - complete success
              message.success(
                responseData?.message || "Out-Scan completed successfully!",
              );
              handleClearAll();
            }
          } else {
            // Handle non-200 status codes
            const errorMessage =
              response.data?.message ||
              response.message ||
              "Failed to submit Out-Scan";
            message.error({
              content: errorMessage,
              duration: 6,
              style: { marginTop: "50px" },
            });
          }
        } catch (error) {
          console.error("Out-Scan submission error:", error);
          message.error("Failed to submit Out-Scan. Please try again.");
        } finally {
          setLoading(false);
        }
      },
    });
  }, [selectedAssignment, scannedShipments, handleClearAll]);

  // Table columns for scanned shipments
  const scannedShipmentsColumns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      width: 150,
      render: (text, record) => (
        <div>
          <span
            style={{
              fontWeight: "600",
              color: record.status === "error" ? "#ff4d4f" : "#1890ff",
            }}
          >
            {text}
          </span>
          {record.status === "error" && (
            <div
              style={{
                marginTop: "4px",
                fontSize: "11px",
                color: "#ff4d4f",
                fontWeight: "500",
              }}
            >
              ❌ FAILED
            </div>
          )}
          {record.status === "success" && (
            <div
              style={{
                marginTop: "4px",
                fontSize: "11px",
                color: "#52c41a",
                fontWeight: "500",
              }}
            >
              ✅ SUCCESS
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Assign To",
      key: "assignTo",
      render: (_, record) =>
        record.assignTo ? (
          <Tag
            color={record.assignTo.type === "HUB" ? "blue" : "green"}
            style={{ fontWeight: "600" }}
          >
            {record.assignTo.type} - {record.assignTo.name}
          </Tag>
        ) : (
          <Tag color="orange">Not Assigned</Tag>
        ),
      width: 200,
    },
    {
      title: "Status / Error Details",
      key: "statusError",
      render: (_, record) => {
        if (record.status === "error" && record.error) {
          return (
            <div
              style={{
                padding: "8px",
                backgroundColor: "#fff2f0",
                borderRadius: "4px",
                border: "1px solid #ffccc7",
                maxWidth: "300px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#cf1322",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                ⚠️ Error Message:
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#595959",
                  lineHeight: "1.3",
                  fontStyle: "italic",
                }}
              >
                {record.error}
              </div>
            </div>
          );
        }
        if (record.status === "success") {
          return (
            <Tag color="green" style={{ fontSize: "11px" }}>
              ✅ Processed Successfully
            </Tag>
          );
        }
        return (
          <Tag color="blue" style={{ fontSize: "11px" }}>
            📋 Ready for Submission
          </Tag>
        );
      },
      width: 320,
    },
    {
      title: "Scanned At",
      dataIndex: "scannedAt",
      key: "scannedAt",
      render: (timestamp) => {
        const date = new Date(timestamp);
        return (
          <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
            {date.toLocaleString()}
          </span>
        );
      },
      width: 140,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeScannedShipment(record.awbNumber)}
          style={{ color: "#ff4d4f" }}
          disabled={record.status === "success"}
        >
          Remove
        </Button>
      ),
      width: 80,
    },
  ];

  return (
    <div className="new-outscan-container">
      <Row justify="space-between" className="header-row">
        <Heading title="Last Mile Out-Scan" />
      </Row>

      <div className="new-outscan-content">
        {/* HUB/CP Selection Section */}
        <div className="assignment-selection-section">
          <Card
            title={
              <div className="section-title">
                <SearchOutlined style={{ marginRight: 8 }} />
                Select Assignment Destination
              </div>
            }
            className="selection-card"
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <Select
                  placeholder="Select type"
                  value={destinationType}
                  onChange={handleDestinationTypeChange}
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="hub">HUB</Option>
                  <Option value="cp">CP</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Select
                  placeholder={`Select ${destinationType.toUpperCase()}`}
                  value={selectedDestination}
                  onChange={handleDestinationSelect}
                  style={{ width: "100%" }}
                  size="large"
                  loading={destinationsLoading}
                  disabled={!destinationType}
                  showSearch
                  filterOption={(input, option) => {
                    // Get the premise name and additional info for filtering
                    if (destinationType === "hub") {
                      const hub = hubs.find(
                        (h) => h.premiseId === option.value,
                      );
                      const searchText = `${hub?.premiseName || ""} ${
                        hub?.hubMode || ""
                      }`;
                      return (
                        searchText.toLowerCase().indexOf(input.toLowerCase()) >=
                        0
                      );
                    } else {
                      const cp = cps.find((c) => c.premiseId === option.value);
                      const searchText = `${cp?.premiseName || ""} ${
                        cp?.city || ""
                      }`;
                      return (
                        searchText.toLowerCase().indexOf(input.toLowerCase()) >=
                        0
                      );
                    }
                  }}
                >
                  {destinationType === "hub"
                    ? hubs.map((hub) => (
                        <Option key={hub.premiseId} value={hub.premiseId}>
                          {hub.premiseName} {hub.hubMode && `(${hub.hubMode})`}
                        </Option>
                      ))
                    : cps.map((cp) => (
                        <Option key={cp.premiseId} value={cp.premiseId}>
                          {cp.premiseName} {cp.city && `(${cp.city})`}
                        </Option>
                      ))}
                </Select>
              </Col>
              {/* <Col xs={24} sm={4} md={8}>
                {selectedAssignment && (
                  <Tag 
                    color={selectedAssignment.type === "HUB" ? "blue" : "green"}
                    style={{ 
                      fontSize: "14px", 
                      padding: "8px 12px",
                      fontWeight: "600"
                    }}
                  >
                    {selectedAssignment.type}: {selectedAssignment.name}
                  </Tag>
                )}
              </Col> */}
            </Row>
          </Card>
        </div>

        {/* AWB Input Section */}
        <div className="awb-input-section">
          <Row justify="center" className="input-row">
            <Col xs={24} sm={20} md={16} lg={12}>
              <div className="awb-input-container">
                <Input
                  ref={awbInputRef}
                  size="large"
                  placeholder={
                    selectedAssignment
                      ? "Scan AWB number using barcode scanner..."
                      : "Please select HUB/CP first before scanning"
                  }
                  value={currentAWB}
                  className="awb-input"
                  disabled={!selectedAssignment}
                  onChange={(e) => {
                    const cleanedValue = e.target.value
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .toUpperCase();
                    setCurrentAWB(cleanedValue);
                  }}
                  onPressEnter={() => handleAWBScan(currentAWB)}
                />
                <div className="scan-info">
                  <span className="scan-count">
                    Total Scanned Shipments:{" "}
                    <strong>{totalScannedCount}</strong>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Scanned Shipments Section with InScan-style Layout */}
        <div className="tables-section">
          <div className="parent-section">
            <Row gutter={[16, 16]} className="tables-row">
              {/* Main Scanned Shipments Table */}
              <Col xs={24}>
                <div className="table-container">
                  <div className="table-header scanned-shipments-header">
                    <div className="header-content">
                      <h3>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Scanned Shipments ({scannedShipments.length})
                      </h3>
                      <div className="header-actions">
                        {scannedShipments.length > 0 && (
                          <Space>
                            <Button
                              type="default"
                              onClick={handleClearAll}
                              icon={<DeleteOutlined />}
                              size="small"
                            >
                              Clear All
                            </Button>
                            <Button
                              type="primary"
                              onClick={handleSubmitOutScan}
                              icon={<SendOutlined />}
                              loading={loading}
                              disabled={
                                !selectedAssignment ||
                                scannedShipments.length === 0
                              }
                              size="small"
                            >
                              Submit Out-Scan ({scannedShipments.length})
                            </Button>
                          </Space>
                        )}
                      </div>
                    </div>
                  </div>
                  <Table
                    rowKey="id"
                    className="scan-table"
                    columns={scannedShipmentsColumns}
                    dataSource={scannedShipments}
                    rowClassName={(record) => {
                      if (record.status === "error") return "error-row";
                      if (record.status === "success") return "success-row";
                      return "";
                    }}
                    pagination={{
                      pageSize: 10,
                      showTotal: (total, range) => {
                        const errorCount = scannedShipments.filter(
                          (s) => s.status === "error",
                        ).length;
                        const successCount = scannedShipments.filter(
                          (s) => s.status === "success",
                        ).length;
                        const pendingCount = scannedShipments.filter(
                          (s) => !s.status,
                        ).length;

                        return (
                          <div style={{ fontSize: "12px" }}>
                            <span>
                              Total: <strong>{total}</strong>
                            </span>
                            {successCount > 0 && (
                              <span
                                style={{ marginLeft: "12px", color: "#52c41a" }}
                              >
                                ✅ Success: <strong>{successCount}</strong>
                              </span>
                            )}
                            {errorCount > 0 && (
                              <span
                                style={{ marginLeft: "12px", color: "#ff4d4f" }}
                              >
                                ❌ Failed: <strong>{errorCount}</strong>
                              </span>
                            )}
                            {pendingCount > 0 && (
                              <span
                                style={{ marginLeft: "12px", color: "#1890ff" }}
                              >
                                📋 Pending: <strong>{pendingCount}</strong>
                              </span>
                            )}
                          </div>
                        );
                      },
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                    }}
                    scroll={{ y: 400, x: 1200 }}
                    size="small"
                    locale={{
                      emptyText: selectedAssignment
                        ? "No shipments scanned yet. Start scanning AWB numbers."
                        : "Please select HUB/CP assignment first to start scanning.",
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Warning for no assignment selected */}
        {!selectedAssignment && (
          <div className="warning-section">
            <Card className="warning-card">
              <div className="warning-content">
                <WarningOutlined
                  style={{
                    color: "#faad14",
                    fontSize: "24px",
                    marginRight: "12px",
                  }}
                />
                <div>
                  <strong>Assignment Required</strong>
                  <p>
                    Please select a HUB or CP assignment before scanning
                    shipments.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewOutScan;
